/**
 * recurrence.js — client-side expansion of schedule recurrence rules
 * (docs/FRONTEND-PLAN.md §2.4, FEA-21).
 *
 * WHY THIS EXISTS
 * ---------------
 * `GET /schedules` windows on the **master record's** stored `startTime` and
 * returns the master only — the backend never expands a recurrence rule
 * (src/services/schedules/handlers.js `handleList`). A calendar therefore has
 * to expand client-side, which is why the calendar page fetches with a
 * 366-day look-back: a weekly meeting created last year is still a master
 * whose stored `startTime` is a year old.
 *
 * Everything in here is pure — no React, no fetch, no `Date.now()` — so it is
 * unit-testable in isolation (`recurrence.test.js`, run with
 * `cd frontend && npx vitest run`).
 *
 * RULE SUPERSET HONOURED
 * ----------------------
 * `{frequency, interval, daysOfWeek, dayOfMonth, endDate, occurrenceCount}` —
 * exactly the fields the create/edit form writes. `weekOfMonth` and
 * `monthOfYear` exist in the documented RecurrenceRule spec but no UI in this
 * app ever writes them and no backend code reads them, so they are
 * deliberately IGNORED here rather than half-implemented; if a rule carrying
 * them ever appears it expands as if they were absent.
 *
 * SEMANTIC DECISIONS (locked, tested)
 * -----------------------------------
 * 1. The master's own `startTime` is ALWAYS occurrence #1, whatever the rule
 *    says. `occurrenceCount` is counted from there, so expansion always walks
 *    from the master even when `rangeStart` is much later — occurrence #6 of a
 *    5-occurrence series is never emitted.
 * 2. Short months are SKIPPED, not clamped: `dayOfMonth: 31` produces nothing
 *    in April or February. Clamping (31 → 30 → 28) makes "the 31st" silently
 *    mean four different days a year, which is unpredictable to a user reading
 *    a grid; skipping matches the common RRULE `BYMONTHDAY` reading, where a
 *    non-existent date simply has no instance. A skipped month is not an
 *    occurrence and therefore does NOT consume `occurrenceCount`.
 * 3. Yearly follows the same rule: a Feb-29 master skips non-leap years.
 * 4. `endDate` is an INCLUSIVE cutoff compared by UTC calendar date — the form
 *    writes a bare `<input type="date">` as UTC midnight, so comparing
 *    instants would drop every same-day occurrence with a real time of day.
 * 5. `endDate` and `occurrenceCount` are mutually exclusive in the UI; if a
 *    record carries both, whichever stops the series first wins.
 * 6. Duration is taken from the master (`endTime - startTime`) and applied to
 *    every occurrence.
 * 7. An occurrence is in range when it INTERSECTS `[rangeStart, rangeEnd)` —
 *    a meeting that started before the window and is still running shows up.
 *    Zero-duration entries are matched on their start instant alone.
 * 8. An unrecognised/absent `frequency` is treated as non-recurring.
 *
 * TIMEZONE CAVEAT (known, accepted for v1)
 * ----------------------------------------
 * Arithmetic is done on the master's UTC instant with fixed steps (day/week
 * granularity as exact millisecond multiples; month/year via UTC calendar
 * fields). The backend stores plain instants and the `timezone` field is
 * carried as an informational IANA string, so this model matches storage —
 * but it means a 09:00-local weekly meeting drifts by an hour across a DST
 * boundary in the local rendering. Fixing that needs per-occurrence
 * local-wall-clock reconstruction in the schedule's own `timezone`, which is
 * out of scope for FEA-21 and would disagree with what the backend stored
 * anyway. The drift is at most one hour and never changes the calendar day
 * except for occurrences within an hour of midnight.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/** Wire order for `daysOfWeek`; index === `Date#getUTCDay()`. */
export const WEEKDAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
];

const FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'];

/**
 * Hard cap on occurrences generated per master per expansion — the guard
 * against a pathological rule (`interval: 1`, no end, huge range) turning a
 * render into a hang. Sized against the calendar's own bounds: the page looks
 * back 366 days and forward one month grid, so a daily series needs ~400
 * instances; 500 clears that with headroom while staying cheap. Occurrences
 * BEFORE `rangeStart` count towards the cap because they must be generated to
 * keep `occurrenceCount` honest.
 */
export const MAX_OCCURRENCES = 500;

/**
 * Iteration guard. Candidate steps are not the same as occurrences: a
 * `dayOfMonth: 31` monthly rule burns ~12 steps per 7 occurrences, and a
 * 7-day weekly rule spends one step per weekday slot. Bounded well above
 * MAX_OCCURRENCES so the cap, not this, is what a user-visible series hits.
 */
const MAX_STEPS = 8000;

function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  if (typeof value !== 'string' || !value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Days in the given UTC year/month (month may overflow; Date.UTC normalises). */
function daysInUtcMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** Whole-UTC-day number, for date-only (not instant) comparisons. */
function utcDayNumber(ms) {
  return Math.floor(ms / DAY_MS);
}

function positiveInt(value) {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n >= 1 ? n : null;
}

/**
 * Fold the wire rule into the closed set of values the expander uses, filling
 * defaults from the master start. Returns null when the schedule should be
 * treated as a one-off.
 */
function normalizeRule(rule, start) {
  if (!rule || typeof rule !== 'object') return null;
  const frequency = String(rule.frequency || '').toLowerCase();
  if (!FREQUENCIES.includes(frequency)) return null;

  const normalized = {
    frequency,
    interval: positiveInt(rule.interval) || 1,
    occurrenceCount: positiveInt(rule.occurrenceCount),
    endDay: null,
    daysOfWeek: null,
    dayOfMonth: null
  };

  const endDate = toDate(rule.endDate);
  if (endDate) normalized.endDay = utcDayNumber(endDate.getTime());

  if (frequency === 'weekly') {
    const days = Array.isArray(rule.daysOfWeek) ? rule.daysOfWeek : [];
    const indexes = [
      ...new Set(
        days
          .map((day) => WEEKDAY_NAMES.indexOf(String(day || '').toLowerCase()))
          .filter((index) => index >= 0)
      )
    ].sort((a, b) => a - b);
    // No (or unrecognised) day list => repeat on the master's own weekday.
    normalized.daysOfWeek = indexes.length ? indexes : [start.getUTCDay()];
  }

  if (frequency === 'monthly') {
    const day = positiveInt(rule.dayOfMonth);
    normalized.dayOfMonth = day && day <= 31 ? day : start.getUTCDate();
  }

  return normalized;
}

/**
 * The `step`-th candidate start for a rule, in epoch ms, or `null` when the
 * step lands on a date that does not exist (short month / non-leap Feb 29).
 * Candidates are non-decreasing in `step` for every frequency, which is what
 * lets the caller stop at the first candidate past the range.
 */
function candidateAt(rule, start, step) {
  const startMs = start.getTime();

  if (rule.frequency === 'daily') {
    return startMs + step * rule.interval * DAY_MS;
  }

  if (rule.frequency === 'weekly') {
    const slots = rule.daysOfWeek;
    // Sunday of the master's week, at the master's time of day.
    const weekStartMs = startMs - start.getUTCDay() * DAY_MS;
    const week = Math.floor(step / slots.length);
    const slot = slots[step % slots.length];
    return weekStartMs + week * rule.interval * WEEK_MS + slot * DAY_MS;
  }

  const hours = start.getUTCHours();
  const minutes = start.getUTCMinutes();
  const seconds = start.getUTCSeconds();
  const millis = start.getUTCMilliseconds();

  if (rule.frequency === 'monthly') {
    const year = start.getUTCFullYear();
    const month = start.getUTCMonth() + step * rule.interval;
    // Normalise the overflowing month so the length check sees the real month.
    const anchor = new Date(Date.UTC(year, month, 1));
    if (daysInUtcMonth(anchor.getUTCFullYear(), anchor.getUTCMonth()) < rule.dayOfMonth) {
      return null; // decision 2: skip, never clamp
    }
    return Date.UTC(
      anchor.getUTCFullYear(),
      anchor.getUTCMonth(),
      rule.dayOfMonth,
      hours,
      minutes,
      seconds,
      millis
    );
  }

  // yearly
  const year = start.getUTCFullYear() + step * rule.interval;
  const month = start.getUTCMonth();
  const day = start.getUTCDate();
  if (daysInUtcMonth(year, month) < day) return null; // decision 3: Feb 29
  return Date.UTC(year, month, day, hours, minutes, seconds, millis);
}

function intersectsRange(startMs, durationMs, fromMs, toMs) {
  if (durationMs <= 0) return startMs >= fromMs && startMs < toMs;
  return startMs < toMs && startMs + durationMs > fromMs;
}

function makeOccurrence(schedule, startMs, durationMs, index) {
  const occurrenceStart = new Date(startMs);
  return {
    schedule,
    occurrenceStart,
    occurrenceEnd: new Date(startMs + durationMs),
    /** 1-based position in the series; 1 is always the master itself. */
    occurrenceIndex: index,
    isRecurring: Boolean(schedule.recurrence),
    /** Stable React key — one master can appear many times in one grid. */
    key: `${schedule.id || schedule.scheduleId || 'schedule'}#${occurrenceStart.toISOString()}`
  };
}

/**
 * Expand one schedule master into the concrete occurrences that intersect
 * `[rangeStart, rangeEnd)`.
 *
 * @param {object} schedule    A Schedule as returned by `GET /schedules`.
 * @param {Date|string|number} rangeStart  Inclusive window start.
 * @param {Date|string|number} rangeEnd    Exclusive window end.
 * @returns {Array<{schedule: object, occurrenceStart: Date, occurrenceEnd: Date,
 *                  occurrenceIndex: number, isRecurring: boolean, key: string}>}
 *          Ascending by `occurrenceStart`. Times are **Date objects**, always
 *          (never ISO strings) — callers format at the edge.
 */
export function expandOccurrences(schedule, rangeStart, rangeEnd) {
  const out = [];
  if (!schedule) return out;

  const start = toDate(schedule.startTime);
  const from = toDate(rangeStart);
  const to = toDate(rangeEnd);
  if (!start || !from || !to) return out;

  const fromMs = from.getTime();
  const toMs = to.getTime();
  if (toMs <= fromMs) return out;

  const end = toDate(schedule.endTime);
  const durationMs = end && end.getTime() > start.getTime() ? end.getTime() - start.getTime() : 0;

  const rule = normalizeRule(schedule.recurrence, start);
  const startMs = start.getTime();

  if (!rule) {
    if (intersectsRange(startMs, durationMs, fromMs, toMs)) {
      out.push(makeOccurrence(schedule, startMs, durationMs, 1));
    }
    return out;
  }

  let count = 0;

  /**
   * Account for one candidate. Returns false when the series is finished and
   * iteration must stop (candidates only ever move forward, so "finished"
   * here is final, not a skip).
   */
  const consider = (ms) => {
    if (rule.endDay !== null && utcDayNumber(ms) > rule.endDay) return false;
    count += 1;
    if (rule.occurrenceCount !== null && count > rule.occurrenceCount) return false;
    if (count > MAX_OCCURRENCES) return false;
    if (ms >= toMs) return false;
    if (intersectsRange(ms, durationMs, fromMs, toMs)) {
      out.push(makeOccurrence(schedule, ms, durationMs, count));
    }
    return true;
  };

  // Decision 1: the master is occurrence #1 even when it does not itself match
  // the pattern (e.g. a Wednesday master with daysOfWeek [tue, thu]).
  if (!consider(startMs)) return out;

  for (let step = 0; step <= MAX_STEPS; step += 1) {
    const ms = candidateAt(rule, start, step);
    if (ms === null) continue; // non-existent date — skipped, costs no count
    if (ms <= startMs) continue; // already emitted as #1, or before the master
    if (!consider(ms)) break;
  }

  return out;
}

/**
 * Expand many masters at once and return one ascending, merged timeline.
 * Convenience only — no behaviour beyond `expandOccurrences` per master.
 */
export function expandAllOccurrences(schedules, rangeStart, rangeEnd) {
  const out = [];
  (schedules || []).forEach((schedule) => {
    expandOccurrences(schedule, rangeStart, rangeEnd).forEach((occurrence) => out.push(occurrence));
  });
  out.sort((a, b) => a.occurrenceStart - b.occurrenceStart);
  return out;
}

/**
 * Human summary of a RecurrenceRule, e.g. "repeats every 2 weeks on monday,
 * wednesday". Pure and display-only; lives here so the rule vocabulary has a
 * single home.
 */
export function describeRecurrence(rule) {
  if (!rule || !rule.frequency) return null;
  const unit = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[rule.frequency];
  if (!unit) return null;
  const interval = positiveInt(rule.interval) || 1;
  const every = interval > 1 ? `every ${interval} ${unit}s` : `every ${unit}`;
  const days = Array.isArray(rule.daysOfWeek) && rule.daysOfWeek.length
    ? ` on ${rule.daysOfWeek.join(', ')}`
    : '';
  const dayOfMonth = rule.dayOfMonth ? ` on day ${rule.dayOfMonth}` : '';
  let until = '';
  if (rule.endDate) {
    const endDate = toDate(rule.endDate);
    until = endDate ? ` until ${endDate.toLocaleDateString()}` : '';
  } else if (rule.occurrenceCount) {
    until = `, ${rule.occurrenceCount} times`;
  }
  return `repeats ${every}${days}${dayOfMonth}${until}`;
}
